import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ReactOtpInput from "react-otp-input";
import Header from "../../commonComponents/header/Header";
import { useState, useEffect } from "react";
import authService from "../../../services/authService";

// Images
import otpLoginImg from "../../../assets/images/otp-right-image.svg";
import emailImg from "../../../assets/images/email-icon.svg";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import Footer from "../../commonComponents/footer/Footer";
import LoadingDialog from "../../commonComponents/loader/LoadingDialog";
import CommonSnackbar from "../../commonComponents/snackbar/CommonSnackbar";
import { getJdId } from "../../../utils/decryptURL";

const ScreenupOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState(null);

  // Get email from navigation state or context
  const email = location.state?.email || "";

  useEffect(() => {
    // Start countdown timer
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const initialValues = {
    emailOtp: "",
    // termsAccepted: false,
  };

  const validationSchema = Yup.object({
    emailOtp: Yup.string()
      .length(6, "OTP must be 6 digits")
      .required("Email OTP is required"),
    // termsAccepted: Yup.boolean().oneOf(
    //   [true],
    //   "You must accept the terms and conditions",
    // ),
  });

  const handleResendOTP = async () => {
    if (!canResend) return;
    if (!email) {
      setSnackbar({
        message: "Missing email information. Please go back and try again.",
        type: "failure",
      });
      return;
    }

    setSnackbar(null);
    setLoading(true);
    setLoaderMessage("Resending OTP...");
    try {
      const companyId = sessionStorage.getItem("companyId") || "";
      const jd_id = getJdId();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/screenup/send-otp`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            company_id: companyId,
            jd_id,
          }),
        },
      );

      const data = await response.json();

      if (response.ok && data?.success !== false) {
        setSnackbar({
          message: data.message || "OTP has been resent to your email!",
          type: "success",
        });
        setResendTimer(60);
        setCanResend(false);
      } else {
        setSnackbar({
          message: data.message || "Failed to resend OTP. Please try again.",
          type: "failure",
        });
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      setSnackbar({
        message: "Network error. Please check your connection and try again.",
        type: "failure",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    const companyId = sessionStorage.getItem("companyId") || "";
    const jdId = sessionStorage.getItem("jdId") || "";
    setLoading(true);
    setLoaderMessage("Verifying OTP...");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/screenup/verify-otp`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            email_otp: values.emailOtp,
            company_id: companyId,
            jd_id: jdId,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();

        setSnackbar({ message: "OTP verified successfully!", type: "success" });
        // Store tokens using authService
        authService.setTokens(data.access_token, data.refresh_token);
        // Store tokens using authService
        authService.setTokens(data.access_token, data.refresh_token);

        // Store tokens in sessionStorage
        sessionStorage.setItem("interviewAuthToken", data.access_token);
        sessionStorage.setItem("interviewRefreshToken", data.refresh_token);

        // Store tokens in localStorage
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        sessionStorage.setItem(
          "userId",
          authService.getUserInfo()?.user_id || "",
        );
        if (data.screenup_data_collection_template) {
          sessionStorage.setItem(
            "screenup_form_template",
            JSON.stringify(data.screenup_data_collection_template),
          );
        }
        sessionStorage.setItem(
          "is_review_required",
          data?.result?.is_review_required || false,
        );
        sessionStorage.setItem(
          "last_date_used",
          data?.result?.last_date_used || "",
        );
        sessionStorage.setItem("candidate_name", data?.name || "");
        sessionStorage.setItem("interview_id", data?.result.interview_id);
        if (data?.isAccepted) {
          const results = data?.result;
          const lastDate = results.last_date_used || null;
          const isExpired = lastDate
            ? new Date(lastDate).setHours(23, 59, 59, 999) < Date.now()
            : false;

          if (isExpired) {
            navigate("/welcome-response", {
              state: {
                resumeId: data.user_id,
                jobDescriptionId: results.job_description_id,
                lastDate,
                candidateName: data.name || null,
                emailSentTo: results.email_sent_to || null,
                interviewId: results.interview_id || null,
                companyName: results.company_name || null,
              },
            });
          } else {
            navigate("/slot-booking", {
              state: {
                jobTitle: results.role,
                companyName: results.company_name,
                lastDate,
                candidateName: data.name,
                resumeId: data.user_id,
                jobDescriptionId: results.job_description_id,
                interviewId: results.interview_id,
                timeDuration: results.time_duration,
                timezone: results.timezone || "Asia/Calcutta",
                isAssessment: false,
              },
            });
          }
        }
        // Navigate to next page
        else navigate("/welcome");
      } else {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        setFieldError(
          "emailOtp",
          errorData.message ||
            errorData?.detail ||
            "Invalid OTP. Please try again.",
        );
        setSnackbar({
          message:
            errorData.message ||
            errorData?.detail ||
            "Invalid OTP. Please try again.",
          type: "failure",
        });
      }
    } catch (error) {
      console.error("Network Error:", error);
      setFieldError(
        "emailOtp",
        "Network error. Please check your connection and try again.",
      );
      setSnackbar({
        message: "Network error. Please check your connection and try again.",
        type: "failure",
      });
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white box-border w-full font-sans pt-4 px-4">
      {loading && (
        <LoadingDialog
          title="Verifying OTP"
          primaryMSG={"Verifying email One-Time Passcode..."}
          bottomMsg="This will take just a bit."
        />
      )}
      {snackbar && (
        <CommonSnackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}

      <Header />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          isValid,
          dirty,
          submitForm,
          touched,
          errors,
        }) => {
          const disabled =
            loading ||
            isSubmitting ||
            !dirty ||
            !isValid ||
            values.emailOtp.length !== 6;
          // !values.termsAccepted;

          return (
            <>
              {/* -------- Main Container -------- */}
              <div className="flex flex-1 flex-col md:flex-row w-full overflow-y-auto">
                {/* Left Section (Image) — hidden on mobile */}
                <div className="mt-10 md:mt-0 md:flex w-full md:w-1/2 justify-center items-center pl-[34px] pr-[50px]">
                  <img
                    src={otpLoginImg}
                    alt="login otp"
                    className="object-contain w-[100%] h-auto rounded-3xl"
                  />
                </div>

                {/* Divider */}
                <div className="hidden md:block border-l border-dividerColor my-[5%]" />
                {/* Right Section (Form) */}
                <div className="w-full md:w-1/2 flex justify-center items-center px-6 py-8 md:p-[70px]">
                  <div className="w-full md:w-[100%]">
                    <h2 className="app-title font-medium mb-2 text-fontColor">
                      One-Time Passcode
                    </h2>
                    <p className="app-body text-fontColor mb-8">
                      We have sent you a One-Time-PIN (OTP) to your Email ID.
                      Please enter the OTP below to Login.
                    </p>

                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!disabled) {
                          submitForm();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.keyCode === 13) {
                          e.preventDefault();
                          if (!disabled) {
                            submitForm();
                          }
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.keyCode === 13) {
                          e.preventDefault();
                          if (!disabled) {
                            submitForm();
                          }
                        }
                      }}
                    >
                      {/* Email OTP */}
                      <div className="flex gap-2 mb-4 items-center">
                        <img src={emailImg} alt="email" className="h-4 mt-6" />
                        <div>
                          <p className="app-textbox ml-2">Email OTP</p>
                          <ReactOtpInput
                            value={values.emailOtp}
                            onChange={(value) =>
                              setFieldValue("emailOtp", value)
                            }
                            numInputs={6}
                            renderInput={(props) => (
                              <input
                                {...props}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                onInput={(e) => {
                                  e.currentTarget.value =
                                    e.currentTarget.value.replace(/\D/g, "");
                                }}
                                onKeyDown={(e) => {
                                  if (props.onKeyDown) {
                                    props.onKeyDown(e);
                                  }
                                  if (e.key === "Enter" || e.keyCode === 13) {
                                    e.preventDefault();
                                    if (!disabled) {
                                      submitForm();
                                    }
                                  }
                                }}
                                onKeyPress={(e) => {
                                  if (props.onKeyPress) {
                                    props.onKeyPress(e);
                                  }
                                  if (e.key === "Enter" || e.keyCode === 13) {
                                    e.preventDefault();
                                    if (!disabled) {
                                      submitForm();
                                    }
                                  }
                                }}
                              />
                            )}
                            inputStyle={{
                              width: "clamp(28px, 8vw, 35px)",
                              height: "clamp(32px, 9vw, 40px)",
                              margin: "0 4px",
                              fontSize: "clamp(14px, 4vw, 18px)",
                              border: "1px solid #111729",
                              borderRadius: "4px",
                              textAlign: "center",
                              fontWeight: 500,
                            }}
                            containerStyle={{ display: "flex" }}
                          />
                        </div>
                      </div>
                      {touched.emailOtp && errors.emailOtp && (
                        <p className="text-errorColor app-textbox mt-3 ml-8">
                          {errors.emailOtp}
                        </p>
                      )}

                      {/* Terms & Conditions */}
                      {/* <div className="flex items-start gap-3 mt-8 mb-4">
                        <input
                          type="checkbox"
                          className="accent-[#111729] transition scale-125 mt-1.5 flex-shrink-0"
                          name="termsAccepted"
                          checked={values.termsAccepted}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <label className="text-tertiaryFontSize text-fontColor leading-relaxed">
                          I have read and agree to the Terms and Conditions as
                          per the Data Privacy Policy and Terms of Use documents
                        </label>
                      </div>
                      {touched.termsAccepted && errors.termsAccepted && (
                        <p className="text-errorColor text-tertiaryFontSize -mt-3 ml-8">
                          {errors.termsAccepted}
                        </p>
                      )} */}

                      {/* Resend OTP Section */}
                      <div className="mt-4 text-left">
                        {/* Resend OTP Section */}
                        <div className="mt-4 text-left pl-8">
                          {canResend ? (
                            <button
                              type="button"
                              onClick={handleResendOTP}
                              disabled={loading}
                              className="text-blue-600 underline hover:underline text-primaryFontSize font-medium disabled:opacity-50"
                            >
                              Resend OTP
                            </button>
                          ) : (
                            <p className="text-primaryFontSize text-fontColor">
                              Resend OTP in{" "}
                              <span className="font-semibold text-logoColor">
                                {resendTimer}s
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Hidden submit button — lets mobile keyboard's Go/Enter key trigger form submission */}
                      <button
                        type="submit"
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          opacity: 0,
                          width: 0,
                          height: 0,
                          border: 0,
                          padding: 0,
                          overflow: "hidden",
                        }}
                        tabIndex={-1}
                      />
                    </Form>
                  </div>
                </div>
              </div>

              {/* -------- Bottom Submit Button -------- */}
              <div className="mt-auto w-full flex justify-end pb-4">
                <button
                  type="button"
                  onClick={submitForm}
                  disabled={disabled}
                  className="flex items-center px-6 py-1.5 btn-size transition-colors duration-200 bg-btnBgColor text-white disabled:opacity-30 disabled:cursor-not-allowed mr-[36px]"
                >
                  Proceed
                  <img src={arrowImg} alt="arrow" className="ml-2" />
                </button>
              </div>
            </>
          );
        }}
      </Formik>

      <Footer />
    </div>
  );
};

export default ScreenupOTP;
